import { useRef, useState, useEffect } from 'react'
import CategoryBadgeComponent from '@/components/ui/buttons/CategoryBadgeComponent'
import styles from '@/components/directory/v2/CategoryManager.module.css'
import { FaCaretLeft, FaCaretRight } from 'react-icons/fa'

const CategoryManager = ({ results }) => {
  const [scrollPosition, setScrollPosition] = useState(0)
  const scrollContainerRef = useRef(null)
  const isDragging = useRef(false)
  const startPosition = useRef(0)
  const startScroll = useRef(0)

  const scrollLeft = () => {
    const newPosition = Math.max(scrollPosition - 150, 0)
    setScrollPosition(newPosition)
  }

  const scrollRight = () => {
    const maxScroll = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth
    const newPosition = Math.min(scrollPosition + 150, maxScroll)
    setScrollPosition(newPosition)
  }

  useEffect(() => {
    scrollContainerRef.current.style.transform = `translateX(-${scrollPosition}px)`
  }, [scrollPosition])

  const handleMouseDown = (e) => {
    isDragging.current = true
    startPosition.current = e.clientX
    startScroll.current = scrollPosition
    scrollContainerRef.current.style.transition = 'none' // Disable transition during drag
    document.body.style.userSelect = 'none' // Prevent text selection
  }

  const handleMouseMove = (e) => {
    if (!isDragging.current) return
    const delta = startPosition.current - e.clientX
    const newPosition = Math.max(
      0,
      Math.min(
        startScroll.current + delta,
        scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth,
      ),
    )
    requestAnimationFrame(() => setScrollPosition(newPosition))
  }

  const handleMouseUp = () => {
    isDragging.current = false
    scrollContainerRef.current.style.transition = 'transform 0.3s ease' // Re-enable transition after drag
    document.body.style.userSelect = 'auto' // Re-enable text selection
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  const handleTouchStart = (e) => {
    isDragging.current = true
    startPosition.current = e.touches[0].clientX
    startScroll.current = scrollPosition
    scrollContainerRef.current.style.transition = 'none' // Disable transition during drag
    document.body.style.userSelect = 'none' // Prevent text selection
  }

  const handleTouchMove = (e) => {
    if (!isDragging.current) return
    const delta = startPosition.current - e.touches[0].clientX
    const newPosition = Math.max(
      0,
      Math.min(
        startScroll.current + delta,
        scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth,
      ),
    )
    requestAnimationFrame(() => setScrollPosition(newPosition))
  }

  const handleTouchEnd = () => {
    isDragging.current = false
    scrollContainerRef.current.style.transition = 'transform 0.3s ease' // Re-enable transition after drag
    document.body.style.userSelect = 'auto' // Re-enable text selection
  }

  const handleWheel = (e) => {
    const maxScroll = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth
    const newPosition = Math.max(0, Math.min(scrollPosition + e.deltaX, maxScroll))
    setScrollPosition(newPosition)
    e.preventDefault() // Prevent default scroll behavior
  }

  useEffect(() => {
    const container = scrollContainerRef.current

    container.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    container.addEventListener('mouseleave', handleMouseUp) // Ensure dragging stops if mouse leaves container

    container.addEventListener('touchstart', handleTouchStart)
    container.addEventListener('touchmove', handleTouchMove)
    container.addEventListener('touchend', handleTouchEnd)

    container.addEventListener('wheel', handleWheel, { passive: false }) // Add wheel event listener

    return () => {
      container.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      container.removeEventListener('mouseleave', handleMouseUp)

      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)

      container.removeEventListener('wheel', handleWheel)
    }
  }, [scrollPosition])

  return (
    <div className={styles.categoryManager}>
      <div className={styles.scrollButton} onClick={scrollLeft}>
        <FaCaretLeft />
      </div>
      <div className={styles.containerWrapper}>
        <div className={styles.container} ref={scrollContainerRef}>
          {results.map((category) => (
            <CategoryBadgeComponent key={category.make} text={category.make} count={category.models.length} />
          ))}
        </div>
      </div>
      <button className={styles.scrollButton} onClick={scrollRight}>
        <FaCaretRight />
      </button>
    </div>
  )
}

export default CategoryManager
